import type { LucideIcon } from 'lucide-react';
import { Building2, FileText, Landmark, Package, Search } from 'lucide-react';
import type { ModuleConfig } from '@/config/modules';

/** Citizen-facing service routes (no login). Shown in the Services menu; FAQ/Support are separate header links. */
export interface PublicServiceMenuItem {
  id: string;
  label: string;
  shortLabel: string;
  description: string;
  path: string;
  icon: LucideIcon;
  /** When set, hidden if the deployment has disabled this module. */
  moduleKey?: keyof ModuleConfig;
}

export const PUBLIC_SERVICE_MENU_ITEMS: PublicServiceMenuItem[] = [
  {
    id: 'address-search',
    label: 'Find a QLS address',
    shortLabel: 'Address search',
    description: 'Search validated addresses and open directions',
    path: '/',
    icon: Search,
  },
  {
    id: 'tourism',
    label: 'Tourism & attractions',
    shortLabel: 'Tourism',
    description: 'Explore verified attractions, stays, and routes',
    path: '/tourism',
    icon: Landmark,
    moduleKey: 'tourismHospitality',
  },
  {
    id: 'permit-application',
    label: 'Building permit application',
    shortLabel: 'Permit application',
    description: 'Apply or track a permit — no account required',
    path: '/permit-licensing/apply',
    icon: FileText,
    moduleKey: 'permitLicensing',
  },
  {
    id: 'business-registration',
    label: 'Business registration',
    shortLabel: 'Business registration',
    description: 'Register a business — save progress with a reference and access code',
    path: '/business-registration',
    icon: Building2,
    moduleKey: 'businessRegistration',
  },
  {
    id: 'delivery-track',
    label: 'Track a parcel',
    shortLabel: 'Parcel tracking',
    description: 'Check delivery status with tracking number and access code',
    path: '/delivery-logistics/track',
    icon: Package,
    moduleKey: 'deliveryLogistics',
  },
  {
    id: 'delivery-service-apply',
    label: 'Join QLS Logistics',
    shortLabel: 'Logistics service',
    description: 'Apply to operate a courier service on the QLS marketplace',
    path: '/delivery-logistics/apply-service',
    icon: Package,
    moduleKey: 'deliveryLogistics',
  },
  {
    id: 'delivery-agent-apply',
    label: 'Become a delivery agent',
    shortLabel: 'Delivery agent',
    description: 'Apply to deliver parcels for approved services',
    path: '/delivery-logistics/apply-agent',
    icon: Package,
    moduleKey: 'deliveryLogistics',
  },
  {
    id: 'waste-service-apply',
    label: 'Join QLS Waste Collection',
    shortLabel: 'Waste service',
    description: 'Apply to collect household waste at validated QLS addresses',
    path: '/waste-management/apply-service',
    icon: FileText,
    moduleKey: 'wasteManagement',
  },
  {
    id: 'waste-agent-apply',
    label: 'Become a waste agent',
    shortLabel: 'Waste agent',
    description: 'Apply to collect waste for approved operators',
    path: '/waste-management/apply-agent',
    icon: FileText,
    moduleKey: 'wasteManagement',
  },
  {
    id: 'utility-service-apply',
    label: 'Join QLS Utility',
    shortLabel: 'Utility service',
    description: 'Apply to operate utility services at validated QLS addresses',
    path: '/utility-management/apply-service',
    icon: FileText,
    moduleKey: 'utilityManagement',
  },
  {
    id: 'utility-agent-apply',
    label: 'Become a utility agent',
    shortLabel: 'Utility agent',
    description: 'Apply to complete utility visits for approved operators',
    path: '/utility-management/apply-agent',
    icon: FileText,
    moduleKey: 'utilityManagement',
  },
];
